// StatTile.jsx — shared "neutral card, colour only for real alerts" stat tile.
// Used across Dashboard, Pipeline, Finance, HR, Care, Clinical, and DME summary
// rows. Background stays neutral (var(--card-bg)) unless tone is 'warn'/'alert',
// so colour only appears where a count genuinely needs attention. See
// CAERA_REBRAND_BUILD_PLAN.md for the design rule this replaces per-page.

import React from 'react';

const TONE = {
  neutral: { fg: 'var(--text)',   label: 'var(--text3)', bg: 'var(--card-bg)',  border: 'var(--border)' },
  warn:    { fg: 'var(--warn)',   label: 'var(--warn)',   bg: 'var(--warn-bg)',   border: 'var(--warn-border)' },
  alert:   { fg: 'var(--danger)', label: 'var(--danger)', bg: 'var(--danger-bg)', border: 'var(--danger-border)' },
  copper:  { fg: 'var(--copper)', label: 'var(--copper)', bg: 'var(--copper-bg)', border: 'var(--copper-border)' },
};

// label:    uppercase caption
// value:    the big number/text
// sub:      optional caption below value (string or node); colored by subTone if given
// tone:     'neutral' | 'warn' | 'alert' — drives bg/border/label/value colour
// subTone:  optional separate tone for `sub` only (e.g. value stays neutral, sub reads "3 overdue" in red)
// dot:      optional colour (hex/var) — renders a small dot before the label for pure-category tiles
// suffix:   optional small text appended after value, e.g. "/5"
// delta:    optional number — renders a ▲/▼ vs-prior-period indicator (success/danger)
// onClick:  optional — makes the tile clickable (e.g. filter navigation)
// compact:  smaller padding/radius variant used by a few dense DME grids
export function StatTile({ label, value, sub, tone = 'neutral', subTone, dot, suffix, delta, onClick, compact = false }) {
  const t = TONE[tone];
  const st = TONE[subTone || 'neutral'];
  return (
    <div
      onClick={onClick}
      style={{
        background: t.bg,
        border: `0.5px solid ${t.border}`,
        borderRadius: compact ? 8 : 10,
        padding: compact ? '9px 12px' : '12px 14px',
        boxShadow: 'var(--shadow-card)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: t.label, marginBottom: 4, display: 'flex', alignItems: 'center', gap: dot ? 6 : 0 }}>
        {dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block', flexShrink: 0 }} />}
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 500, color: t.fg }}>
        {value}
        {suffix && <span style={{ fontSize: 11, fontWeight: 400 }}>{suffix}</span>}
      </div>
      {sub && <div style={{ fontSize: 10, color: subTone ? st.fg : 'var(--text3)', marginTop: 2 }}>{sub}</div>}
      {delta != null && (
        <div style={{ fontSize: 10, marginTop: 4, color: delta >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>
          {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}% vs prior period
        </div>
      )}
    </div>
  );
}

export default StatTile;
